/* eslint-disable */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useAppData } from '../../context/AppDataContext';
import { paymentsAPI, expensesAPI, companyExpensesAPI, receiptsAPI, normaliseExpense, tenantsAPI } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import { PROPERTY_EXPENSE_CATEGORIES, categoryOptions, categoryHint } from '../../utils/expenseCategories';
import CompanyPicker from '../companies/CompanyPicker';
import { companiesAPI } from '../../services/api';
import { useSearchParams } from 'react-router-dom';

// Same canonical list used on the property Expenses tab — kept identical so
// categorisation stays consistent everywhere (including the Portfolio Health
// Check report, which matches on exact category names).
const EXPENSE_CATEGORIES = PROPERTY_EXPENSE_CATEGORIES;

// BUG FIX: company expenses used this same property-focused list — Gas
// Safety, HMO Licence Fees etc. genuinely don't apply to a company-level
// expense. Sourced from CompanyExpenses.js's own CATEGORIES constant,
// which is what a company expense was actually being categorised against
// before that screen's functionality moved into this file.
const COMPANY_EXPENSE_CATEGORIES = ['Accountancy','Insurance','Legal','Marketing','Office','Other','Software','Travel'];

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStr = (d) => (d || todayStr()).substring(0,7);

export default function Transactions() {
  const { properties, payments, setPayments, expenses, setExpenses, companyExpenses, setCompanyExpenses } = useAppData();
  // BUG FIX: This component previously derived a payment's displayed
  // tenant from the room's CURRENT occupant, ignoring the payment's own
  // tenant_id link — same root cause as the equivalent bug fixed in
  // PropertyDetail.js's Payments tab. Loaded locally since tenants aren't
  // part of the shared AppDataContext.
  const [tenants, setTenants] = useState([]);
  useEffect(() => { tenantsAPI.getAll().then(setTenants).catch(() => {}); }, []);
  // PERF: Year-collapse grouping — rendering every row flat becomes slow
  // once a portfolio has real history (especially after backdating a full
  // fiscal year). Prior years default to collapsed; current year stays
  // open. Logged to backlog as the first step of a larger data-loading
  // review — see PERF/SCALE backlog entry.
  const [collapsedYears, setCollapsedYears] = useState(new Set());
  const activeProps = properties.filter(p => !p.archived);

  const [filterProperty, setFilterProperty] = useState('all'); // 'all' | 'company' | 'personal' | 'companyowned' | 'co:<id>' | propertyId

  // COMPANY-006: the retired /company route redirects here with
  // ?filter=company so the old Company Expenses bookmark lands on the
  // equivalent view rather than a generic list.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setFilterProperty(f);
  }, [searchParams]);

  // COMPANY-005: companies are fetched here rather than read from
  // AppDataContext because the context list is only populated at app start —
  // a company created in Administration would otherwise not appear in this
  // filter until a full page reload. Same reasoning as CompanyPicker.
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    let cancelled = false;
    companiesAPI.getAll()
      .then(rows => { if (!cancelled) setCompanies(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setCompanies([]); });
    return () => { cancelled = true; };
  }, []);
  const activeCompanies = companies.filter(c => !(c.archived === true || c.archived === 'true'));
  const isMultiCompany = activeCompanies.length > 1;
  const companyById = React.useMemo(() => {
    const m = {}; companies.forEach(c => { m[String(c.id)] = c; }); return m;
  }, [companies]);
  const companyNameFor = (id) => (id ? (companyById[String(id)]?.name || null) : null);
  const [filterType, setFilterType] = useState('all'); // 'all' | 'income' | 'expense'
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // { kind: 'payment'|'expense'|'companyExpense', row }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [addedCount, setAddedCount] = useState(0);
  // Forces a genuinely fresh form on every "Save & Add Another" — React
  // fully tears down and rebuilds it each time, avoiding any risk of
  // carried-over native input/validation state between entries.
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [modalSuccess, setModalSuccess] = useState('');
  const [addedLog, setAddedLog] = useState([]); // persistent list of what's been added this session — doesn't auto-hide, so it's always clear what actually saved
  const [viewingReceiptId, setViewingReceiptId] = useState(null);

  // AI-003: Receipt / agency statement scanning — scans every selected file
  // up front, then shows ONE combined review screen for everything found
  // across all of them, rather than clicking through page by page.
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanStep, setScanStep] = useState('upload'); // 'upload' | 'scanning' | 'reviewing'
  const [scanFiles, setScanFiles] = useState([]); // files picked for this session
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0 });
  const [scanDragActive, setScanDragActive] = useState(false);
  const [scanDuplicateWarnings, setScanDuplicateWarnings] = useState([]); // one per file, if any
  const [scanFileErrors, setScanFileErrors] = useState([]); // files that failed to scan
  const [scanError, setScanError] = useState('');
  const [reviewItems, setReviewItems] = useState([]);
  const [scanPreviews, setScanPreviews] = useState({}); // receiptId -> { url, isPdf, name }
  const [savingReview, setSavingReview] = useState(false);

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  // Build one unified row list from the three existing data sources — no new
  // backend endpoint needed, this reads the exact same data every other
  // screen already loads via AppDataContext.
  const rows = useMemo(() => {
    const propById = Object.fromEntries(activeProps.map(p => [String(p.id), p]));
    // BUG FIX: for a payment with NO tenant_id at all (predates the
    // tenant-linking feature, or was entered without picking a tenant),
    // this fell straight back to the room's CURRENT cached tenant name —
    // exactly the original bug, just for the subset of payments that were
    // never explicitly linked. This mirrors the date-bounded fallback
    // already built server-side for the tenant-detail payments endpoint:
    // find whichever tenant's own occupancy (lease_start through
    // lease_end/move_out_date, whichever's earlier) actually covers the
    // date this payment was made, rather than assuming it's whoever lives
    // there today.
    // BUG FIX: found via direct data check — several moved-out tenants have
    // NO lease_start recorded at all, only a move_out_date (this predates
    // today's work; confirmed the move-out action itself never touches
    // lease_start, so it was simply never captured when they were added).
    // The original version of this fallback required lease_start and so
    // could never match these tenants against any historical payment,
    // regardless of the date — every one of their payments fell through
    // to the room's current occupant. Now tries a precise match first
    // (lease_start defined and covers the date), and only falls back to
    // "no lease_start, but paid on/before their move_out_date, and this is
    // the closest such candidate" when no precise match exists — so a
    // tenant with a real recorded start is always preferred over a guess.
    const findTenantByDate = (propId, roomId, paidDate) => {
      if (!paidDate) return null;
      const candidates = (tenants || []).filter(t => {
        if (String(t.property_id || t.propertyId) !== String(propId)) return false;
        if (roomId && String(t.room_id || t.roomId) !== String(roomId)) return false;
        return true;
      });
      const precise = candidates.find(t => {
        if (!t.lease_start || t.lease_start > paidDate) return false;
        const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean);
        if (candidateEnds.length && candidateEnds.sort()[0] < paidDate) return false;
        return true;
      });
      if (precise) return precise;
      // BUG FIX: only checked move_out_date — a tenant with no lease_start
      // AND no move_out_date (but WITH lease_end, e.g. a legacy record
      // marked moved_out through a path that never set move_out_date)
      // could never match here either, falling through to the wrong
      // room-cache name regardless. Mirrors the server-side fix: use
      // whichever of move_out_date / lease_end is actually set.
      const undated = candidates
        .filter(t => !t.lease_start && (t.move_out_date || t.lease_end) && (t.move_out_date || t.lease_end) >= paidDate)
        .sort((a,b) => (a.move_out_date||a.lease_end).localeCompare(b.move_out_date||b.lease_end));
      return undated[0] || null;
    };
    const out = [];

    payments.forEach(p => {
      const prop = propById[String(p.propertyId || p.property_id)];
      const room = prop?.rooms?.find(r => String(r.id) === String(p.roomId || p.roomId));
      const linkedTenant = (p.tenantId || p.tenant_id) ? tenants.find(t => String(t.id) === String(p.tenantId || p.tenant_id)) : null;
      const byDateTenant = !linkedTenant ? findTenantByDate(p.propertyId || p.property_id, p.roomId || p.room_id, p.paidDate || p.paid_date) : null;
      const resolvedTenant = linkedTenant || byDateTenant;
      const tenantLabel = resolvedTenant
        ? `${resolvedTenant.first_name} ${resolvedTenant.last_name||''}`.trim() + (resolvedTenant.status==='moved_out' ? ' (moved out)' : '')
        : room?.tenantName;
      out.push({
        kind: 'payment', id: p.id, date: (p.paidDate || p.paid_date || '').substring(0,10), type: 'income', receiptId: p.receipt_id || p.receiptId || null,
        propertyId: p.propertyId || p.property_id, propertyLabel: prop?.address || 'Unknown property', ownership: prop?.ownership || null,
        // COMPANY-005: derived from the property, never stored on the payment.
        // Reassigning a property to a different company therefore moves its
        // whole transaction history with it and can never drift out of sync.
        // Guarded on ownership so a legacy row carrying a stale company_id on
        // a personally-held property cannot leak into a company view.
        companyId: prop && String(prop.ownership||'').toLowerCase() === 'company' ? (prop.company_id || prop.companyId || null) : null,
        currency: prop?.currency || 'GBP',
        category: 'Rent', description: room ? (tenantLabel ? `Rent — ${tenantLabel}${room.roomName ? ' (' + room.roomName + ')' : ''}` : `Rent — ${room.roomName || 'Room'}`) : 'Rent',
        amount: parseFloat(p.amount || 0), paid: true, month: p.month, roomId: p.roomId || p.room_id,
        raw: p,
      });
    });

    expenses.forEach(e => {
      const prop = propById[String(e.propertyId || e.property_id)];
      out.push({
        kind: 'expense', id: e.id, date: (e.date||'').substring(0,10), type: 'expense', receiptId: e.receipt_id || e.receiptId || null,
        propertyId: e.propertyId || e.property_id, propertyLabel: prop?.address || 'Unknown property', ownership: prop?.ownership || null,
        companyId: prop && String(prop.ownership||'').toLowerCase() === 'company' ? (prop.company_id || prop.companyId || null) : null,
        currency: prop?.currency || 'GBP',
        category: e.category, description: e.description || '', amount: parseFloat(e.amount || 0),
        paid: e.paid !== false, raw: e,
      });
    });

    (companyExpenses || []).forEach(e => {
      out.push({
        kind: 'companyExpense', id: e.id, date: (e.date||'').substring(0,10), type: 'expense', receiptId: e.receipt_id || e.receiptId || null,
        propertyId: null, propertyLabel: 'Company Expenses', companyId: e.company_id || e.companyId || null,
        // COMPANY-005: was hardcoded GBP. A company expense belongs to a
        // company, and companies carry their own currency — an Irish entity's
        // overheads are in EUR and were being rendered with a pound sign.
        currency: (e.company_id || e.companyId) ? (companyById[String(e.company_id || e.companyId)]?.currency || 'GBP') : 'GBP',
        category: e.category, description: e.description || '', amount: parseFloat(e.amount || 0),
        paid: e.paid !== false, raw: e,
      });
    });

    return out.sort((a,b) => (b.date||'').localeCompare(a.date||''));
  }, [activeProps, payments, expenses, companyExpenses, tenants, companyById]);

  // COMPANY-005: 'co:<id>' scopes to a single company — that company's
  // property transactions AND its own company expenses together, which is the
  // view you need to prepare one entity's accounts.
  const companyFilterId = String(filterProperty).startsWith('co:') ? String(filterProperty).slice(3) : null;

  const filteredRows = rows.filter(r => {
    if (companyFilterId) {
      // Selecting a specific company hides personal/joint property
      // transactions entirely, as intended.
      if (String(r.companyId || '') !== companyFilterId) return false;
    }
    if (filterProperty === 'company' && r.propertyId !== null) return false;
    // BUG: 'personal' excluded JOINTLY-owned properties, because it tested for
    // exactly 'personal'. Joint ownership is two or more individuals — S24
    // applies exactly as it does to sole personal ownership — so it belongs in
    // this bucket. It was silently invisible in every view except "All".
    if (filterProperty === 'personal' && !['personal','joint'].includes(String(r.ownership||'').toLowerCase())) return false;
    if (filterProperty === 'companyowned' && r.ownership !== 'company') return false;
    if (!companyFilterId && !['all','company','personal','companyowned'].includes(filterProperty) && String(r.propertyId) !== String(filterProperty)) return false;
    if (filterType !== 'all' && r.type !== filterType) return false;
    if (filterFrom && (r.date||'') < filterFrom) return false;
    if (filterTo && (r.date||'') > filterTo) return false;
    if (search && !`${r.description} ${r.category} ${r.propertyLabel}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by year for collapsible rendering. Current year defaults open;
  // all other years default collapsed the first time data loads. After
  // that, toggling is a plain add/remove — no special-casing on read.
  const currentYear = String(new Date().getFullYear());
  const rowsByYear = useMemo(() => {
    const groups = {};
    filteredRows.forEach(r => {
      const yr = r.date ? r.date.substring(0,4) : 'Unknown';
      if (!groups[yr]) groups[yr] = [];
      groups[yr].push(r);
    });
    return Object.entries(groups).sort(([a],[b]) => b.localeCompare(a));
  }, [filteredRows]);
  const seededYears = useRef(false);
  useEffect(() => {
    if (seededYears.current || rowsByYear.length === 0) return;
    seededYears.current = true;
    setCollapsedYears(new Set(rowsByYear.map(([yr]) => yr).filter(yr => yr !== currentYear)));
  }, [rowsByYear]);
  const toggleYear = (yr) => setCollapsedYears(prev => {
    const next = new Set(prev);
    if (next.has(yr)) next.delete(yr); else next.add(yr);
    return next;
  });

  // BUG FIX: These used to sum raw numbers across ALL rows regardless of
  // currency, then display the result with a single (always GBP) symbol —
  // meaningless once a portfolio has both GBP and EUR properties, and the
  // direct cause of Irish properties appearing to show £ instead of €.
  // Now grouped by currency; the stat cards render one block per currency
  // actually present in the current filtered view, rather than a single
  // misleading blended total.
  const totalsByCurrency = {};
  filteredRows.forEach(r => {
    if (!totalsByCurrency[r.currency]) totalsByCurrency[r.currency] = { income: 0, expense: 0 };
    if (r.type === 'income') totalsByCurrency[r.currency].income += r.amount;
    else totalsByCurrency[r.currency].expense += r.amount;
  });
  const currenciesPresent = Object.keys(totalsByCurrency);

  // AI-003: Receipt / agency statement scanning handlers
  const openScan = () => {
    // Clean up any blob URLs from a previous session before starting fresh
    Object.values(scanPreviews).forEach(p => { try { URL.revokeObjectURL(p.url); } catch {} });
    setScanPreviews({});
    setScanStep('upload');
    setScanFiles([]);
    setScanProgress({ current: 0, total: 0 });
    setScanError('');
    setScanDragActive(false);
    setScanDuplicateWarnings([]);
    setScanFileErrors([]);
    setReviewItems([]);
    setShowScanModal(true);
  };

  // Scans every selected file, one after another, collecting ALL line items
  // from ALL of them into a single combined list before showing anything to
  // review. Each item remembers which file/receipt it came from, so saving
  // afterwards can still correctly group by receipt.
  const handleScanSubmit = async () => {
    if (!scanFiles.length) return setScanError('Please choose at least one receipt or statement');
    setScanStep('scanning');
    setScanError('');
    setScanDuplicateWarnings([]);
    setScanFileErrors([]);
    const allRows = [];
    const dupWarnings = [];
    const fileErrors = [];
    const previews = {};

    for (let i = 0; i < scanFiles.length; i++) {
      setScanProgress({ current: i + 1, total: scanFiles.length });
      try {
        const result = await receiptsAPI.scan(scanFiles[i]);
        if (result.duplicateWarning) dupWarnings.push({ file: scanFiles[i].name, ...result.duplicateWarning });
        // The file is already sitting in the browser — no need to fetch it
        // back from the server for a preview, just make a local blob URL
        // straight from what was picked. Instant, and never leaves the browser.
        previews[result.receiptId] = {
          url: URL.createObjectURL(scanFiles[i]),
          isPdf: scanFiles[i].type === 'application/pdf',
          name: scanFiles[i].name,
        };
        (result.lineItems || []).forEach(item => {
          allRows.push({
            include: true,
            receiptId: result.receiptId,
            sourceFile: scanFiles[i].name,
            type: item.type,
            amount: item.amount ?? '',
            date: item.date || todayStr(),
            description: item.description || '',
            category: item.category || EXPENSE_CATEGORIES[0],
            propertyId: item.matchedPropertyId ? String(item.matchedPropertyId) : (activeProps[0]?.id ? String(activeProps[0].id) : ''),
            roomId: item.matchedRoomId ? String(item.matchedRoomId) : '',
            assignTo: 'property',
            companyId: null,
            tenantNameGuess: item.tenantNameGuess || '',
          });
        });
      } catch (err) {
        fileErrors.push({ file: scanFiles[i].name, error: err.error || 'Failed to scan' });
      }
    }

    setScanPreviews(previews);
    setScanDuplicateWarnings(dupWarnings);
    setScanFileErrors(fileErrors);
    setReviewItems(allRows);
    if (!allRows.length) {
      setScanError('Nothing could be read from the file(s) selected. You can still add these manually.');
      setScanStep('upload');
    } else {
      setScanStep('reviewing');
    }
  };

  const updateReviewItem = (idx, patch) => {
    setReviewItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));
  };

  // Saves everything in one action — groups the reviewed items by which
  // receipt they came from (since /confirm still works per-receipt) but
  // this is invisible to the user, who just sees one button and one result.
  const handleSaveReview = async () => {
    setScanError('');
    const included = reviewItems.filter(r => r.include);
    if (!included.length) return setScanError('Select at least one line item to save');
    for (const r of included) {
      if (!r.amount || parseFloat(r.amount) <= 0) return setScanError('Every included row needs a valid amount');
      if (!r.date) return setScanError('Every included row needs a date');
      if (r.type === 'income' && !r.roomId) return setScanError('Every included income row needs a room/tenant selected');
      if (r.type === 'expense' && r.assignTo === 'property' && !r.propertyId) return setScanError('Every included expense row needs a property selected');
    }
    setSavingReview(true);
    try {
      const byReceipt = {};
      included.forEach(r => { (byReceipt[r.receiptId] = byReceipt[r.receiptId] || []).push(r); });

      let totalPayments = 0, totalExpenses = 0;
      const allSkipped = [];
      for (const [receiptId, rows] of Object.entries(byReceipt)) {
        const payload = rows.map(r => ({
          type: r.type,
          amount: parseFloat(r.amount),
          date: r.date,
          description: r.description,
          category: r.category,
          propertyId: r.type === 'income' ? r.propertyId : (r.assignTo === 'property' ? r.propertyId : null),
          roomId: r.type === 'income' ? r.roomId : undefined,
          assignTo: r.type === 'expense' ? r.assignTo : undefined,
          companyId: r.assignTo === 'company' ? r.companyId : undefined,
        }));
        const result = await receiptsAPI.confirm(receiptId, payload);
        totalPayments += result.created.payments;
        totalExpenses += result.created.expenses + result.created.companyExpenses;
        if (result.skipped?.length) allSkipped.push(...result.skipped);
      }

      // BUG FIX: Previously the frontend just trusted a bare count — a line
      // that silently failed to save (e.g. missing property) vanished with
      // no trace. The backend now returns exactly what was skipped and why,
      // and we surface that clearly here rather than hiding it.
      if (allSkipped.length > 0) {
        setScanError(`⚠️ ${allSkipped.length} item(s) did NOT save: ` + allSkipped.map(s => `"${s.label}" (${s.reason})`).join('; '));
        setSavingReview(false);
        return; // stay on screen so the warning is actually seen
      }

      const [freshPayments, freshExpenses, freshCompanyExpenses] = await Promise.all([
        paymentsAPI.getAll(), expensesAPI.getAll(), companyExpensesAPI.getAll(),
      ]);
      setPayments(freshPayments);
      setExpenses(freshExpenses);
      setCompanyExpenses(freshCompanyExpenses);
      setShowScanModal(false);
      flash(`✅ Saved — ${totalPayments} payment(s), ${totalExpenses} expense(s) added from ${scanFiles.length} receipt${scanFiles.length!==1?'s':''}`);
    } catch (err) { setScanError(err.error || 'Failed to save reviewed items'); }
    finally { setSavingReview(false); }
  };

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const filterSummary = [
      String(filterProperty).startsWith('co:') ? (companyNameFor(String(filterProperty).slice(3)) || 'Company') :
      filterProperty === 'all' ? 'All properties & company' :
      filterProperty === 'personal' ? 'Personal & joint properties only' :
      filterProperty === 'companyowned' ? 'Company-owned properties only' :
      filterProperty === 'company' ? 'Company expenses only' :
      activeProps.find(p => String(p.id) === filterProperty)?.address || 'Filtered',
      filterType !== 'all' ? (filterType === 'income' ? 'Income only' : 'Expenses only') : null,
      filterFrom ? `From ${filterFrom}` : null,
      filterTo ? `To ${filterTo}` : null,
      search ? `Search: "${search}"` : null,
    ].filter(Boolean).join(' | ');

    const rows = [
      ['TRANSACTIONS REPORT'],
      [`Filters: ${filterSummary}`],
      [`Generated: ${todayStr()}`],
      [],
      ['Date', 'Type', 'Property / Company', 'Category', 'Description', 'Currency', 'Amount', 'Paid'],
      ...filteredRows.map(r => [
        r.date || '', r.type === 'income' ? 'Income' : 'Expense', r.propertyLabel, r.category,
        r.description || '', r.currency, (r.type === 'income' ? r.amount : -r.amount).toFixed(2), r.paid ? 'Yes' : 'No',
      ]),
      [],
      ...currenciesPresent.flatMap(cur => [
        [`TOTAL INCOME (${cur})`, '', '', '', '', cur, totalsByCurrency[cur].income.toFixed(2), ''],
        [`TOTAL EXPENSES (${cur})`, '', '', '', '', cur, totalsByCurrency[cur].expense.toFixed(2), ''],
        [`NET (${cur})`, '', '', '', '', cur, (totalsByCurrency[cur].income - totalsByCurrency[cur].expense).toFixed(2), ''],
        [],
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [12, 10, 30, 26, 30, 10, 12, 8].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, `casavello-Transactions-${todayStr()}.xlsx`);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ txType:'expense', assignTo: activeProps[0]?.id ? String(activeProps[0].id) : 'company', companyId:null, roomId:'', category:EXPENSE_CATEGORIES[0], amount:'', date:todayStr(), description:'', paid:true });
    setError('');
    setAddedCount(0);
    setAddedLog([]);
    setModalSuccess('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    if (row.kind === 'payment') {
      setForm({ txType:'income', assignTo: String(row.propertyId), roomId: row.roomId||'', amount:row.amount, date:row.date, month: row.month });
    } else if (row.kind === 'expense') {
      setForm({ txType:'expense', assignTo: String(row.propertyId), category:row.category, amount:row.amount, date:row.date, description:row.description, paid:row.paid });
    } else {
      setForm({ txType:'expense', assignTo:'company', companyId:row.companyId, category:row.category, amount:row.amount, date:row.date, description:row.description, paid:row.paid });
    }
    setError('');
    setShowModal(true);
  };

  const selectedProperty = form.assignTo && form.assignTo !== 'company' ? activeProps.find(p => String(p.id) === String(form.assignTo)) : null;

  // Shared by all three actions — Save, Save & Add Another both call this;
  // Cancel doesn't (it never saves anything). `closeAfter` controls what
  // happens once the save succeeds: true closes the modal (Save), false
  // resets the form and stays open for another entry (Save & Add Another).
  const performSave = async (closeAfter) => {
    setError('');
    if (!form.amount || parseFloat(form.amount) <= 0) return setError('Enter a valid amount');
    if (!form.date) return setError('Date is required');
    setSaving(true);
    try {
      if (form.txType === 'income') {
        if (!form.assignTo || form.assignTo === 'company') return setError('Income must be assigned to a property');
        if (!form.roomId) return setError('Select a room/tenant');
        const payload = { propertyId: form.assignTo, roomId: form.roomId, month: monthStr(form.date), amount: parseFloat(form.amount), paidDate: form.date };
        if (editing) {
          const updated = await paymentsAPI.update(editing.id, payload);
          setPayments(prev => prev.map(p => p.id === editing.id ? updated : p));
        } else {
          const created = await paymentsAPI.create(payload);
          setPayments(prev => [created, ...prev]);
        }
      } else {
        // ITEM-005: Paid defaults to Yes unless explicitly unticked
        const paidVal = form.paid !== undefined ? form.paid : true;
        if (form.assignTo === 'company') {
          const payload = { category: form.category, amount: parseFloat(form.amount), date: form.date, description: form.description||'', paid: paidVal, companyId: form.companyId||null };
          if (editing) {
            const updated = await companyExpensesAPI.update(editing.id, payload);
            setCompanyExpenses(prev => prev.map(x => x.id === editing.id ? updated : x));
          } else {
            const created = await companyExpensesAPI.create(payload);
            setCompanyExpenses(prev => [created, ...prev]);
          }
        } else {
          if (!form.assignTo) return setError('Select a property or Company Expenses');
          const payload = { propertyId: form.assignTo, category: form.category, amount: parseFloat(form.amount), date: form.date, description: form.description||'', paid: paidVal };
          if (editing) {
            const updated = await expensesAPI.update(editing.id, payload);
            setExpenses(prev => prev.map(x => x.id === editing.id ? normaliseExpense(updated) : x));
          } else {
            const created = await expensesAPI.create(payload);
            setExpenses(prev => [normaliseExpense(created), ...prev]);
          }
        }
      }
      if (editing) {
        setShowModal(false);
        flash('Transaction updated');
      } else if (closeAfter) {
        // BUG FIX: "Done" used to be the same button as "Cancel", just
        // relabeled — so clicking it on an entry you'd actually filled in
        // (but hadn't saved yet) silently discarded it instead of saving,
        // since Cancel never saves anything. Save/Cancel/Save & Add
        // Another are now three distinct, honestly-labelled actions.
        setShowModal(false);
        flash(addedCount > 0 ? `Saved — ${addedCount + 1} transaction${addedCount+1!==1?'s':''} added this session` : 'Transaction added');
      } else {
        // Save & Add Another — stay open, reset the amount/description/room
        // but keep the property/company/category selection since
        // consecutive entries while reconciling a bank statement are often
        // for the same one.
        const entryCurrency = selectedProperty?.currency || 'GBP';
        const savedLabel = form.txType === 'income'
          ? `💰 ${formatCurrency(parseFloat(form.amount), entryCurrency)} rent`
          : `📤 ${formatCurrency(parseFloat(form.amount), entryCurrency)} ${form.category}`;
        setAddedLog(prev => [...prev, savedLabel]);
        setForm(p => ({ ...p, amount:'', description:'', roomId:'' }));
        setAddedCount(c => c + 1);
        setFormInstanceKey(k => k + 1);
        setModalSuccess('✅ Added — keep going, or click Save when finished');
        setTimeout(() => setModalSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.error || 'Failed to save transaction');
    }
    finally { setSaving(false); }
  };

  // Enter key in a text field triggers native form submission — treat that
  // the same as clicking Save (close after), the more conservative default.
  const handleSubmit = (e) => { e.preventDefault(); performSave(true); };

  const handleViewReceipt = async (receiptId) => {
    setViewingReceiptId(receiptId);
    try {
      const { url } = await receiptsAPI.download(receiptId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) { alert(err.error || 'Failed to open the original receipt'); }
    finally { setViewingReceiptId(null); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete this ${row.type === 'income' ? 'payment' : 'expense'}? This cannot be undone.`)) return;
    try {
      if (row.kind === 'payment') { await paymentsAPI.delete(row.id); setPayments(prev => prev.filter(p => p.id !== row.id)); }
      else if (row.kind === 'expense') { await expensesAPI.delete(row.id); setExpenses(prev => prev.filter(e => e.id !== row.id)); }
      else { await companyExpensesAPI.delete(row.id); setCompanyExpenses(prev => prev.filter(e => e.id !== row.id)); }
      flash('Deleted');
    } catch (err) { alert(err.error || 'Failed to delete'); }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">💷 Transactions</h1>
          <span className="page-subtitle">Every payment and expense across your whole portfolio in one place — built for bank reconciliation</span>
        </div>
        <div style={{ display:'flex', gap:'0.75rem' }}>
          <button className="btn btn-secondary" onClick={handleExport}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>
          <button className="btn btn-secondary" onClick={openScan}>📷 Scan Receipt</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Transaction</button>
        </div>
      </div>

      {message && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{message}</div>}

      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        {currenciesPresent.length === 0 ? (
          <>
            <div className="stat-card"><div className="stat-label">Total Income</div><div className="stat-value positive">{formatCurrency(0)}</div></div>
            <div className="stat-card"><div className="stat-label">Total Expenses</div><div className="stat-value" style={{color:'var(--danger)'}}>{formatCurrency(0)}</div></div>
            <div className="stat-card"><div className="stat-label">Net</div><div className="stat-value">{formatCurrency(0)}</div></div>
          </>
        ) : currenciesPresent.map(cur => {
          const t = totalsByCurrency[cur];
          const net = t.income - t.expense;
          return (
            <React.Fragment key={cur}>
              <div className="stat-card"><div className="stat-label">Total Income {currenciesPresent.length > 1 ? `(${cur})` : ''}</div><div className="stat-value positive">{formatCurrency(t.income, cur)}</div></div>
              <div className="stat-card"><div className="stat-label">Total Expenses {currenciesPresent.length > 1 ? `(${cur})` : ''}</div><div className="stat-value" style={{color:'var(--danger)'}}>{formatCurrency(t.expense, cur)}</div></div>
              <div className="stat-card"><div className="stat-label">Net {currenciesPresent.length > 1 ? `(${cur})` : ''}</div><div className="stat-value" style={{color: net>=0 ? 'var(--success)':'var(--danger)'}}>{formatCurrency(net, cur)}</div></div>
            </React.Fragment>
          );
        })}
        <div className="stat-card"><div className="stat-label">Transactions Shown</div><div className="stat-value">{filteredRows.length}</div></div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'center' }}>
          <select value={filterProperty} onChange={e=>setFilterProperty(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)' }}>
            <option value="all">All Properties &amp; Company</option>
            <option value="personal">Personal &amp; Joint Properties Only</option>
            <option value="companyowned">Company-Owned Properties Only</option>
            <option value="company">Company Expenses Only</option>
            {/* COMPANY-005: one entry per company — its properties and its own
                overheads together. Only shown when there is genuinely more
                than one company; with a single company these rows would
                duplicate "Company-Owned Properties Only". */}
            {isMultiCompany && (
              <optgroup label="By Company">
                {activeCompanies.map(c => <option key={`co-${c.id}`} value={`co:${c.id}`}>{c.name}</option>)}
              </optgroup>
            )}
            <optgroup label="By Property">
              {activeProps.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
            </optgroup>
          </select>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)' }}>
            <option value="all">All Types</option>
            <option value="income">Income Only</option>
            <option value="expense">Expenses Only</option>
          </select>
          <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)' }} title="From date" />
          <span style={{ color:'var(--text-secondary)' }}>to</span>
          <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)' }} title="To date" />
          <input type="text" placeholder="Search description, category, property…" value={search} onChange={e=>setSearch(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)', flex:1, minWidth:'220px' }} />
        </div>
      </div>

      {filteredRows.length === 0 ? (
        <div className="empty-state"><span>💷</span><h3>No Transactions Match</h3><p>Try adjusting your filters, or add your first transaction.</p></div>
      ) : (
        <div className="table-container"><table className="table">
          {/* COMPANY-005: the Company column only appears when there is more
              than one company — a single-company landlord would see the same
              name repeated on every row for no information gain. */}
          <thead><tr><th>Date</th><th>Type</th><th>Property / Company</th>{isMultiCompany && <th>Company</th>}<th>Category</th><th>Description</th><th>Amount</th><th>Paid</th><th>Actions</th></tr></thead>
          <tbody>{rowsByYear.map(([yr, yearRows]) => {
            const collapsed = collapsedYears.has(yr);
            const yearIncome = yearRows.filter(r=>r.type==='income').reduce((s,r)=>s+r.amount,0);
            const yearExpense = yearRows.filter(r=>r.type==='expense').reduce((s,r)=>s+r.amount,0);
            const yearCurrencies = [...new Set(yearRows.map(r=>r.currency))];
            const netLabel = yearCurrencies.length <= 1
              ? formatCurrency(yearIncome - yearExpense, yearCurrencies[0] || 'GBP')
              : yearCurrencies.map(c => formatCurrency(yearRows.filter(r=>r.currency===c && r.type==='income').reduce((s,r)=>s+r.amount,0) - yearRows.filter(r=>r.currency===c && r.type==='expense').reduce((s,r)=>s+r.amount,0), c)).join(' + ');
            return (
              <React.Fragment key={yr}>
                <tr onClick={()=>toggleYear(yr)} style={{ cursor:'pointer', background:'var(--bg-light)' }}>
                  {/* COMPANY-005: colSpan must track the optional Company column,
                      otherwise the year-group header row under-spans and the
                      table visibly breaks alignment when multi-company. */}
                  <td colSpan={isMultiCompany ? 9 : 8} style={{ fontWeight:700, padding:'0.6rem 0.75rem' }}>
                    {collapsed ? '▶' : '▼'} {yr} <span style={{ fontWeight:400, color:'var(--text-secondary)', fontSize:'0.85rem' }}>({yearRows.length} transaction{yearRows.length!==1?'s':''} · net {netLabel})</span>
                  </td>
                </tr>
                {!collapsed && yearRows.map((r,i) => (
            <tr key={`${r.kind}-${r.id}-${i}`}>
              <td>{r.date ? new Date(r.date.substring(0,10)+'T12:00:00').toLocaleDateString('en-GB') : '—'}</td>
              <td>{r.type==='income' ? <span className="badge badge-success">Income</span> : <span className="badge badge-secondary">Expense</span>}</td>
              <td>{r.propertyLabel}</td>
              {isMultiCompany && (
                <td style={{ fontSize:'0.8rem' }}>
                  {companyNameFor(r.companyId) || <span style={{ color:'var(--text-secondary)' }}>{r.propertyId === null ? 'Unassigned' : 'Personal'}</span>}
                </td>
              )}
              <td>{r.category}</td>
              <td style={{ maxWidth:'220px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.description || <span style={{color:'var(--border)'}}>—</span>}</td>
              <td style={{ fontWeight:600, color: r.type==='income' ? 'var(--success)' : 'inherit' }}>{r.type==='income'?'+':'-'}{formatCurrency(r.amount, r.currency)}</td>
              <td>{r.paid ? <span className="badge badge-success">Yes</span> : <span className="badge badge-danger">No</span>}</td>
              <td>
                <div style={{ display:'flex', gap:'0.4rem' }}>
                  {r.receiptId && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize:'0.75rem', padding:'0.25rem 0.5rem' }}
                      onClick={()=>handleViewReceipt(r.receiptId)}
                      disabled={viewingReceiptId === r.receiptId}
                      title="View original receipt"
                    >
                      {viewingReceiptId === r.receiptId ? '⏳' : '📎'}
                    </button>
                  )}
                  <button className="btn btn-secondary" style={{ fontSize:'0.75rem', padding:'0.25rem 0.5rem' }} onClick={()=>openEdit(r)}>✏️</button>
                  <button className="btn btn-danger" style={{ fontSize:'0.75rem', padding:'0.25rem 0.5rem' }} onClick={()=>handleDelete(r)}>🗑️</button>
                </div>
              </td>
            </tr>
                ))}
              </React.Fragment>
            );
          })}</tbody>
        </table></div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:'520px' }}>
            <div className="modal-header">
              <span className="modal-title">
                {editing ? '✏️ Edit Transaction' : '➕ Add Transaction'}
                {!editing && addedCount > 0 && <span style={{ fontSize:'0.8rem', fontWeight:400, color:'var(--text-secondary)', marginLeft:'0.5rem' }}>({addedCount} added this session)</span>}
              </span>
              <button className="modal-close" onClick={()=>setShowModal(false)}>×</button>
            </div>
            {error && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{error}</div>}
            {modalSuccess && <div className="alert alert-success" style={{ marginBottom:'1rem' }}>{modalSuccess}</div>}
            {addedLog.length > 0 && (
              <div style={{ background:'var(--bg-light)', border:'1px solid var(--border)', borderRadius:'0.5rem', padding:'0.6rem 0.85rem', marginBottom:'1rem', fontSize:'0.8rem' }}>
                <strong>Added this session ({addedLog.length}):</strong>
                <ul style={{ margin:'0.35rem 0 0', paddingLeft:'1.2rem' }}>
                  {addedLog.map((label, i) => <li key={i}>{label}</li>)}
                </ul>
              </div>
            )}
            <form key={formInstanceKey} onSubmit={handleSubmit}>
              {!editing && (
                <div className="form-group">
                  <label>Transaction Type</label>
                  <div style={{ display:'flex', gap:'0.75rem' }}>
                    <button type="button" onClick={()=>setForm(p=>({...p, txType:'income', assignTo: activeProps[0]?.id ? String(activeProps[0].id) : ''}))} className={`btn ${form.txType==='income'?'btn-success':'btn-secondary'}`} style={{ flex:1 }}>💰 Income (Rent)</button>
                    <button type="button" onClick={()=>setForm(p=>({...p, txType:'expense'}))} className={`btn ${form.txType==='expense'?'btn-primary':'btn-secondary'}`} style={{ flex:1 }}>📤 Expense</button>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Assign To *</label>
                <select value={form.assignTo||''} onChange={e=>{
                  const newAssignTo = e.target.value;
                  const validCategories = newAssignTo==='company' ? COMPANY_EXPENSE_CATEGORIES : EXPENSE_CATEGORIES;
                  setForm(p=>({ ...p, assignTo:newAssignTo, roomId:'', category: validCategories.includes(p.category) ? p.category : validCategories[0] }));
                }} disabled={!!editing && editing.kind==='payment'}>
                  {form.txType==='expense' && <option value="company">Company Expenses</option>}
                  {activeProps.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                </select>
              </div>

              {form.txType==='expense' && form.assignTo==='company' && (
                <div className="form-group">
                  <label>Which Company (optional)</label>
                  <CompanyPicker value={form.companyId} onChange={(id)=>setForm(p=>({...p, companyId:id}))} />
                </div>
              )}

              {form.txType==='income' && selectedProperty && (
                <div className="form-group">
                  <label>Room / Tenant *</label>
                  <select value={form.roomId||''} onChange={e=>setForm(p=>({...p, roomId:e.target.value}))}>
                    <option value="">Select room…</option>
                    {(selectedProperty.rooms||[]).map(r => <option key={r.id} value={r.id}>{r.tenantName ? `${r.tenantName}${r.roomName ? ' (' + r.roomName + ')' : ''}` : (r.roomName || `Room ${r.id}`)}</option>)}
                  </select>
                </div>
              )}

              {form.txType==='expense' && (
                <div className="form-group">
                  <label>Category *</label>
                  <select value={form.category} onChange={e=>setForm(p=>({...p, category:e.target.value}))} required>
                    {(form.assignTo==='company' ? COMPANY_EXPENSE_CATEGORIES : categoryOptions(form.category)).map(c => <option key={c}>{c}</option>)}
                  </select>
                  {form.assignTo!=='company' && categoryHint(form.category) && <small style={{display:'block',marginTop:'0.25rem',color:'var(--text-secondary)'}}>{categoryHint(form.category)}</small>}
                </div>
              )}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" step="0.01" value={form.amount||''} onChange={e=>setForm(p=>({...p, amount:e.target.value}))} onWheel={e=>e.target.blur()} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={form.date||''} onChange={e=>setForm(p=>({...p, date:e.target.value}))} />
                </div>
              </div>

              {form.txType==='expense' && (
                <div className="form-group">
                  <label>Description</label>
                  <input type="text" value={form.description||''} onChange={e=>setForm(p=>({...p, description:e.target.value}))} placeholder="Optional — helps identify this later" />
                </div>
              )}

              {form.txType==='expense' && (
                <div className="form-group">
                  <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer' }}>
                    <input type="checkbox" checked={form.paid !== false} onChange={e=>setForm(p=>({...p, paid:e.target.checked}))} />
                    Paid
                  </label>
                </div>
              )}

              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem', position:'sticky', bottom:0, background:'#fff', padding:'0.75rem 0', borderTop:'1px solid var(--border)' }}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                {!editing && (
                  <button type="button" className="btn btn-secondary" disabled={saving} onClick={()=>performSave(false)}>{saving ? 'Saving...' : '➕ Save & Add Another'}</button>
                )}
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : (editing ? '💾 Save Changes' : '💾 Save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── SCAN RECEIPT MODAL ── */}
      {showScanModal && (
        <div className="modal-overlay" onClick={()=>setShowScanModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth: scanStep==='reviewing' ? '1400px' : '520px', width: scanStep==='reviewing' ? '95vw' : undefined, maxHeight: '92vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <span className="modal-title">📷 Scan Receipt / Statement</span>
              <button className="modal-close" onClick={()=>setShowScanModal(false)}>×</button>
            </div>

            {scanStep === 'upload' && (
              <div>
                <p style={{ color:'var(--text-secondary)', marginBottom:'1.25rem', lineHeight:1.5 }}>
                  Upload photos or PDFs of receipts, or letting agency rent statements — select or drop several at once if you have a batch to get through. If a statement covers several tenants' rent and several deductions (agency fee, cleaning, repairs etc.), casavello pulls out every line item for you to review together before anything is saved.
                </p>
                {scanError && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{scanError}</div>}
                <div
                  onDragOver={e => { e.preventDefault(); setScanDragActive(true); }}
                  onDragLeave={e => { e.preventDefault(); setScanDragActive(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    setScanDragActive(false);
                    const dropped = Array.from(e.dataTransfer.files || []);
                    const valid = dropped.filter(f => ['image/jpeg','image/png','application/pdf'].includes(f.type));
                    if (valid.length) { setScanFiles(valid); setScanError(''); }
                    else if (dropped.length) { setScanError('Please drop JPEG, PNG or PDF files only.'); }
                  }}
                  onClick={() => document.getElementById('scan-file-input').click()}
                  className="card"
                  style={{
                    border: `2px dashed ${scanDragActive ? 'var(--brand-green)' : 'var(--border)'}`,
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '2.5rem 1.5rem',
                    background: scanDragActive || scanFiles.length ? 'var(--bg-light)' : 'transparent',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    id="scan-file-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={e => { setScanFiles(Array.from(e.target.files || [])); setScanError(''); }}
                    style={{ display: 'none' }}
                  />
                  {scanFiles.length > 0 ? (
                    <div>
                      <span style={{ fontSize: '2.5rem' }}>✅</span>
                      <h3 style={{ margin:'0.5rem 0 0.25rem', color:'var(--brand-green)' }}>{scanFiles.length} file{scanFiles.length!==1?'s':''} ready to scan</h3>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', margin:0 }}>{scanFiles.map(f=>f.name).join(', ')}</p>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.8rem', marginTop:'0.5rem' }}>Click or drop again to choose different files</p>
                    </div>
                  ) : (
                    <div>
                      <span style={{ fontSize: '2.5rem' }}>📄</span>
                      <h3 style={{ margin:'0.5rem 0 0.25rem' }}>Drag and drop receipts here</h3>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', margin:0 }}>or click to browse — select multiple at once — JPEG, PNG or PDF</p>
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1.5rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={()=>setShowScanModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleScanSubmit} disabled={!scanFiles.length}>
                    🔍 Scan {scanFiles.length > 1 ? `${scanFiles.length} Documents` : 'Document'}
                  </button>
                </div>
              </div>
            )}

            {scanStep === 'scanning' && (
              <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
                <div className="loading-spinner" style={{ margin:'0 auto 1.5rem' }}></div>
                <h3 style={{ margin:'0 0 0.5rem' }}>Reading document {scanProgress.current} of {scanProgress.total}…</h3>
                <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem' }}>{scanFiles[scanProgress.current-1]?.name}</p>
              </div>
            )}

            {scanStep === 'reviewing' && (() => {
              const includedCount = reviewItems.filter(r => r.include).length;
              // BUG FIX: same currency-mixing issue as the main Transactions
              // list — group by each item's own property currency rather
              // than summing everything under one (always GBP) symbol.
              const reviewTotalsByCurrency = {};
              reviewItems.filter(r => r.include).forEach(r => {
                const cur = activeProps.find(p => String(p.id) === r.propertyId)?.currency || 'GBP';
                if (!reviewTotalsByCurrency[cur]) reviewTotalsByCurrency[cur] = { income: 0, expense: 0 };
                reviewTotalsByCurrency[cur][r.type] += parseFloat(r.amount) || 0;
              });
              const reviewCurrencies = Object.keys(reviewTotalsByCurrency);

              // Group by receipt — keeps each document's own income and
              // expense lines together, so you can visually cross-check them
              // against that document's preview side by side, rather than
              // hunting across two separate tables for items from the same receipt.
              const receiptIds = [...new Set(reviewItems.map(r => r.receiptId))];
              const groups = receiptIds.map(receiptId => ({
                receiptId,
                preview: scanPreviews[receiptId],
                rows: reviewItems.map((item, idx) => ({ item, idx })).filter(x => x.item.receiptId === receiptId),
              }));

              return (
                <div>
                  {scanFileErrors.length > 0 && (
                    <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>
                      ⚠️ Couldn't read: {scanFileErrors.map(f => `"${f.file}" (${f.error})`).join('; ')} — everything else scanned fine.
                    </div>
                  )}
                  {scanDuplicateWarnings.length > 0 && (
                    <div style={{ background:'#fef3c7', border:'1px solid #fde047', borderRadius:'0.5rem', padding:'0.85rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#92400e' }}>
                      ⚠️ {scanDuplicateWarnings.map((w,i) => (
                        <div key={i}>"{w.file}" looks the same as one uploaded {new Date(w.uploadedAt).toLocaleDateString('en-GB')} ("{w.filename}"){w.wasConfirmed ? ', already saved' : ''} — double-check you're not recording this twice.</div>
                      ))}
                    </div>
                  )}
                  {scanError && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{scanError}</div>}

                  <div className="stats-grid" style={{ marginBottom:'1.25rem' }}>
                    <div className="stat-card"><div className="stat-label">Receipts Scanned</div><div className="stat-value">{scanFiles.length}</div></div>
                    {reviewCurrencies.map(cur => (
                      <React.Fragment key={cur}>
                        <div className="stat-card"><div className="stat-label">Income Selected {reviewCurrencies.length > 1 ? `(${cur})` : ''}</div><div className="stat-value positive">{formatCurrency(reviewTotalsByCurrency[cur].income, cur)}</div></div>
                        <div className="stat-card"><div className="stat-label">Expenses Selected {reviewCurrencies.length > 1 ? `(${cur})` : ''}</div><div className="stat-value" style={{color:'var(--danger)'}}>{formatCurrency(reviewTotalsByCurrency[cur].expense, cur)}</div></div>
                      </React.Fragment>
                    ))}
                    <div className="stat-card"><div className="stat-label">Items Selected</div><div className="stat-value">{includedCount} of {reviewItems.length}</div></div>
                  </div>

                  <div style={{ maxHeight:'480px', overflowY:'auto' }}>
                    {groups.map(group => (
                      <div key={group.receiptId} className="card" style={{ marginBottom:'1.25rem' }}>
                        <div style={{ fontWeight:700, marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                          📄 {group.preview?.name || 'Receipt'}
                          <span className="badge badge-secondary" style={{ fontWeight:400 }}>{group.rows.length} item{group.rows.length!==1?'s':''}</span>
                        </div>
                        <div style={{ display:'flex', gap:'1.25rem', alignItems:'flex-start', flexWrap:'wrap' }}>
                          {/* Visual preview — lets you check what casavello read against the actual document before signing off */}
                          <div style={{ flexShrink:0 }}>
                            {group.preview?.isPdf ? (
                              <a href={group.preview.url} target="_blank" rel="noreferrer" style={{ display:'block', width:'160px', border:'1px solid var(--border)', borderRadius:'0.5rem', padding:'1rem', textAlign:'center', textDecoration:'none', color:'var(--text-secondary)' }}>
                                <div style={{ fontSize:'2rem' }}>📄</div>
                                <div style={{ fontSize:'0.75rem', fontWeight:600, marginTop:'0.4rem' }}>View Original PDF</div>
                              </a>
                            ) : group.preview ? (
                              <a href={group.preview.url} target="_blank" rel="noreferrer">
                                <img src={group.preview.url} alt={group.preview.name} style={{ width:'160px', height:'auto', maxHeight:'220px', objectFit:'contain', border:'1px solid var(--border)', borderRadius:'0.5rem', display:'block' }} />
                                <div style={{ fontSize:'0.7rem', color:'var(--text-secondary)', textAlign:'center', marginTop:'0.3rem' }}>Click to enlarge</div>
                              </a>
                            ) : (
                              <div style={{ width:'160px', height:'120px', border:'1px dashed var(--border)', borderRadius:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)', fontSize:'0.75rem' }}>No preview</div>
                            )}
                          </div>

                          {/* This receipt's own income + expense lines together */}
                          <div style={{ flex:1, minWidth:'420px' }}>
                            <div className="table-container"><table className="table">
                              <thead><tr><th></th><th>Type</th><th>Amount</th><th>Date</th><th>Description</th><th>Category / Room</th><th>Assign To</th></tr></thead>
                              <tbody>{group.rows.map(({item, idx}) => {
                                const prop = activeProps.find(p => String(p.id) === item.propertyId);
                                return (
                                  <tr key={idx} style={{ opacity: item.include ? 1 : 0.5 }}>
                                    <td><input type="checkbox" checked={item.include} onChange={e=>updateReviewItem(idx,{include:e.target.checked})} /></td>
                                    <td>{item.type==='income' ? <span className="badge badge-success">Income</span> : <span className="badge badge-secondary">Expense</span>}</td>
                                    <td><input type="number" step="0.01" value={item.amount} onChange={e=>updateReviewItem(idx,{amount:e.target.value})} onWheel={e=>e.target.blur()} style={{ width:'85px' }} /></td>
                                    <td><input type="date" value={item.date} onChange={e=>updateReviewItem(idx,{date:e.target.value})} style={{ width:'130px' }} /></td>
                                    <td>
                                      <input type="text" value={item.description} onChange={e=>updateReviewItem(idx,{description:e.target.value})} style={{ width:'130px' }} />
                                      {item.tenantNameGuess && <div style={{ fontSize:'0.7rem', color:'var(--text-secondary)' }}>detected: "{item.tenantNameGuess}"</div>}
                                    </td>
                                    {item.type === 'income' ? (
                                      <>
                                        <td>
                                          <select value={item.roomId} onChange={e=>updateReviewItem(idx,{roomId:e.target.value})} style={{ width:'130px', borderColor: !item.roomId ? 'var(--danger)' : undefined }}>
                                            <option value="">Select room…</option>
                                            {(prop?.rooms||[]).map(r => <option key={r.id} value={r.id}>{r.tenantName ? `${r.tenantName}${r.roomName ? ' (' + r.roomName + ')' : ''}` : (r.roomName || `Room ${r.id}`)}</option>)}
                                          </select>
                                        </td>
                                        <td>
                                          <select value={item.propertyId} onChange={e=>updateReviewItem(idx,{propertyId:e.target.value, roomId:''})} style={{ width:'140px' }}>
                                            {activeProps.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                                          </select>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td>
                                          <select value={item.category} onChange={e=>updateReviewItem(idx,{category:e.target.value})} style={{ width:'160px' }}>
                                            {(item.assignTo==='company' ? COMPANY_EXPENSE_CATEGORIES : EXPENSE_CATEGORIES).map(c => <option key={c}>{c}</option>)}
                                          </select>
                                        </td>
                                        <td>
                                          <select value={item.assignTo} onChange={e=>{
                                            const newAssignTo = e.target.value;
                                            const validCategories = newAssignTo==='company' ? COMPANY_EXPENSE_CATEGORIES : EXPENSE_CATEGORIES;
                                            updateReviewItem(idx,{assignTo:newAssignTo, category: validCategories.includes(item.category) ? item.category : validCategories[0]});
                                          }} style={{ width:'140px', marginBottom:'0.3rem' }}>
                                            <option value="property">Property</option>
                                            <option value="company">Company Expenses</option>
                                          </select>
                                          {item.assignTo === 'property' ? (
                                            <select value={item.propertyId} onChange={e=>updateReviewItem(idx,{propertyId:e.target.value})} style={{ width:'140px', borderColor: !item.propertyId ? 'var(--danger)' : undefined }}>
                                              {activeProps.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                                            </select>
                                          ) : (
                                            <CompanyPicker value={item.companyId} onChange={(id)=>updateReviewItem(idx,{companyId:id})} style={{ width:'140px', padding:'0.4rem', border:'1px solid var(--border)', borderRadius:'0.375rem' }} />
                                          )}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}</tbody>
                            </table></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1.25rem' }}>
                    <button type="button" className="btn btn-secondary" onClick={()=>setShowScanModal(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleSaveReview} disabled={savingReview}>
                      {savingReview ? 'Saving…' : `✅ Save All Selected (${includedCount})`}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
